import { Link } from 'react-router-dom';
import { Bed, Car, Maximize, MapPin, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatCurrency, formatArea } from '@/lib/format';
import { PROPERTY_PURPOSES } from '@/types/property';
import type { Property } from '@/types/property';
import { cn } from '@/lib/utils';

interface PropertyCardProps {
  property: Property;
  className?: string;
}

export function PropertyCard({ property, className }: PropertyCardProps) {
  const mainImage = property.main_image_url || 
    property.property_images?.find(img => img.is_main)?.image_url ||
    property.property_images?.[0]?.image_url ||
    'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=800&q=80';

  return (
    <Card className={cn('group overflow-hidden bg-card border-border h-full flex flex-col transition-all duration-500 hover:shadow-xl hover:-translate-y-2', className)}>
      {/* Image */}
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={mainImage}
          alt={property.title}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        
        {/* Badges */}
        <div className="absolute left-3 top-3 flex flex-wrap gap-2">
          {property.is_featured && (
            <Badge className="bg-primary text-primary-foreground text-xs">
              EM DESTAQUE
            </Badge>
          )}
        </div>
      </div>

      <CardContent className="flex flex-1 flex-col p-5">
        {/* Location */}
        <div className="mb-2 flex items-center gap-1 text-xs text-muted-foreground">
          <MapPin className="h-3 w-3 flex-shrink-0" />
          <span className="line-clamp-1">
            {property.city}{property.state && ` - ${property.state}`}
          </span>
        </div>

        {/* Title */}
        <h3 className="mb-2 font-bold text-lg text-foreground line-clamp-2 leading-tight">
          {property.title}
        </h3>

        {/* Neighborhood */}
        <p className="mb-3 text-sm text-muted-foreground">
          {property.neighborhood}
        </p>

        {/* Features */}
        <div className="mb-4 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
          {property.total_area && (
            <div className="flex items-center gap-1">
              <Maximize className="h-3.5 w-3.5" />
              <span>{formatArea(property.total_area)}</span>
            </div>
          )}
          {property.suites && property.suites > 0 && (
            <div className="flex items-center gap-1">
              <Bed className="h-3.5 w-3.5" />
              <span>{property.suites} Suítes</span>
            </div>
          )}
          {property.parking_spots > 0 && (
            <div className="flex items-center gap-1">
              <Car className="h-3.5 w-3.5" />
              <span>{property.parking_spots} Vagas</span>
            </div>
          )}
        </div>

        {/* Description */}
        {property.description && (
          <p className="mb-4 line-clamp-3 text-sm text-muted-foreground leading-relaxed flex-1">
            {property.description}
          </p>
        )}

        {/* Price */}
        <div className="mb-4 mt-auto">
          <p className="text-xl font-bold text-primary">
            {formatCurrency(property.price)}
            {property.purpose === 'locacao' && (
              <span className="text-sm font-normal text-muted-foreground">/mês</span>
            )}
          </p>
        </div>

        {/* CTA Button */}
        <Link to={`/imoveis/${property.slug || property.id}`}>
          <Button 
            variant="outline" 
            className="w-full gap-2 border-border text-foreground hover:border-primary hover:text-primary"
          >
            VER DETALHES <ArrowRight className="h-4 w-4" />
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}
